import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { healthApi } from '@/api/healthApi'
import { productVersion } from '@/help/registry'
import { describeBuild, devVersionSuffix } from '@/lib/buildIdentity'
import { isDevDeployment } from '@/lib/deployEnvironment'

/**
 * The one product version, shown just before the tenant selector in the TopBar
 * and in the DashboardPage header (which hides the TopBar and carries its own).
 * Web and API share a single counter, so there is one number here, never two.
 *
 * On dev (and locally) it also says how far dev is past staging:
 * `v10.01.011-dev.3` is 3 merges into dev that staging does not have yet, web
 * and API together, with each side's commit and count in the tooltip. See
 * `@/lib/buildIdentity`. Staging, production and client deployments show the
 * plain number and make no extra request.
 *
 * Each side names the newest release tag in its own repo, which is why the
 * tooltip can name two different tags.
 *
 * Deliberately quiet: small, muted and borderless, so it reads as a label next
 * to the tenant control rather than competing with it.
 *
 * Import boundary: this pulls in `@/help/registry`, which eagerly globs every
 * markdown page in the wiki at build time. Only desktop-only modules may import
 * it, and nothing under `src/mobile/**` ever may (see the 250 KB mobile budget
 * in CLAUDE.md).
 */
export function VersionChip() {
  // Read per render, not at module scope, for the same reason as EnvironmentBadge.
  const dev = isDevDeployment(window.location.hostname)
  const web = dev ? __BUILD_IDENTITY__ : null
  const { data: api = null } = useQuery({
    queryKey: ['build-identity', 'api'],
    queryFn: healthApi.version,
    enabled: dev,
    staleTime: Infinity,
    // A label only: an older API or a network blip just leaves the line out.
    retry: false,
  })

  if (!productVersion) return null

  const title = ['Release notes', web && describeBuild('web', web), api && describeBuild('api', api)]
    .filter(Boolean)
    .join('\n')

  return (
    <Link
      to={`/wiki/${productVersion.urlPath}`}
      title={title}
      className='shrink-0 font-mono text-[11px] leading-none text-muted-foreground/70 transition-colors hover:text-foreground'
    >
      v{productVersion.version}
      {devVersionSuffix(web, api)}
    </Link>
  )
}
